import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { detectTrafficSource } from '@/lib/trafficSource';

function sessionId(): string {
  try {
    let id = sessionStorage.getItem('zifek_sid');
    if (!id) {
      id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('zifek_sid', id);
    }
    return id;
  } catch {
    return 'anon';
  }
}

/** Segment d'hôte du site public (domaine perso ou sous-domaine du marchand). */
function hostOf(): string {
  try {
    return typeof window !== 'undefined' ? window.location.hostname : '';
  } catch {
    return '';
  }
}

/**
 * Enregistre une visite du site public d'un marchand.
 * Une seule ligne par session (visitesiteweb) pour éviter le spam,
 * et incrémente le compteur de la page d'atterrissage du site.
 *
 * NB : les tables historiques `visitesiteweb` / `visitelandingpage` ont des
 * colonnes `ip` en NOT NULL ; on y écrit une valeur vide (l'IP réelle est
 * captée côté serveur par l'Edge Function `theme-visit-track`).
 */
export default function TenantVisitTracker() {
  const { tenant, isTenant } = useTenant();

  useEffect(() => {
    if (!isTenant || !tenant) return;
    const key = `zifek_visit_${tenant.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch { /* stockage indisponible */ }

    const now = new Date().toISOString();
    // Provenance réelle du visiteur (Google, WhatsApp, Facebook, Instagram…).
    const traffic = detectTrafficSource();
    // Écriture best-effort : ne bloque jamais l'affichage de la boutique.
    void supabase
      .from('visitesiteweb')
      .insert({
        boutique_nom: tenant.nomcommerce || tenant.name || '',
        subdomain: tenant.user_name || '',
        boutique_id: tenant.id,
        source: traffic.source,
        visiteur: sessionId(),
        ip: '',
        date: now,
      })
      .then(() => {});

    void supabase
      .from('visitelandingpage')
      .insert({ ip: '', date: now })
      .then(() => {});
  }, [isTenant, tenant]);

  return null;
}

/**
 * Enregistre la vue d'un produit sur le site public du marchand.
 * Alimente « Produits les plus consultés » et « Visites produit ».
 */
export function ProductViewTracker() {
  const { id } = useParams<{ id: string }>();
  const { tenant, isTenant } = useTenant();

  useEffect(() => {
    if (!isTenant || !tenant || !id) return;
    const key = `zifek_pv_${tenant.id}_${id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch { /* stockage indisponible */ }

    const productId = parseInt(id, 10);
    if (Number.isNaN(productId)) return;

    (async () => {
      try {
        const { data: product } = await supabase
          .from('product_items')
          .select('name')
          .eq('id', productId)
          .maybeSingle();
        const name = product?.name || `Produit #${productId}`;
        const now = new Date();
        const dateOnly = now.toISOString().slice(0, 10);
        const host = hostOf();

        await supabase.from('zifekproductstats').insert({
          produit: name,
          ip: '',
          date: dateOnly,
          temps: now.toISOString(),
        });

        await supabase.from('visiteproduitsiteweb').insert({
          id_produit: productId,
          date: now.toISOString(),
          temps: now.toISOString(),
          lien: `/product/${productId}`,
          ip: '',
          type: 'produit',
          page: `/product/${productId}`,
          user_id: String(tenant.id),
          boutique: tenant.nomcommerce || tenant.name || '',
          boutique_id: tenant.id,
          source: detectTrafficSource().source,
          sessionid: sessionId(),
          sessionname: '',
          sce: '',
          siteweb: host,
        });
      } catch {
        /* best-effort */
      }
    })();
  }, [isTenant, tenant, id]);

  return null;
}